<script setup lang="ts">
/**
 * The head of a page: an optional kicker above the title (the area — „Admin ·
 * Speicher“), the title, a subtitle, and the page's actions on the right.
 * `icon` shows the page's icon in a brand tile in front of the title block;
 * the `leading` slot sits there too (a back button, before the tile), the
 * default slot under the subtitle (chips, meta), and `toolbar` in a row of its
 * own under the head (filters, search).
 *
 * `error` shows a danger line under the head (role alert); `hint` a muted one
 * when there is no error. For the whole page frame — head plus a scrolling
 * body — use NxPageShell.
 *
 * `density="compact"` is the app-header size (NxWorkspaceHeader): a smaller
 * title, tighter gaps, actions centred on the title row. `liveSubtitle` makes
 * the subtitle a polite status region (a sync or count line) that exists even
 * while empty, so its changes are announced.
 */
import { computed, type Component } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    kicker?: string;
    error?: string | null;
    hint?: string | null;
    /** Heading level of the title. Defaults to 1. */
    level?: 1 | 2 | 3;
    /** `compact`: the app-header size (smaller title, tighter gaps). Defaults to `default`. */
    density?: 'default' | 'compact';
    /** Render the subtitle as a polite status region, present even while empty. */
    liveSubtitle?: boolean;
    /**
     * The page's icon, shown in a brand tile in front of the title block — a
     * page that stands for one thing (Formica ID). Decorative: the title names
     * the page. Smaller in `compact` density.
     */
    icon?: Component;
  }>(),
  {
    subtitle: '',
    kicker: '',
    error: null,
    hint: null,
    level: 1,
    density: 'default',
    liveSubtitle: false,
    icon: undefined,
  }
);

const heading = computed(() => `h${props.level}`);
</script>

<template>
  <div
    class="nx-page-header"
    :class="{ 'is-compact': props.density === 'compact' }"
    data-testid="nx-page-header"
  >
    <header class="nx-page-header__hero">
      <div v-if="$slots.leading || props.icon" class="nx-page-header__leading">
        <slot name="leading" />
        <span v-if="props.icon" class="nx-page-header__tile" aria-hidden="true">
          <component
            :is="props.icon"
            :class="props.density === 'compact' ? 'nxt-icon-md' : 'nxt-icon-xl'"
          />
        </span>
      </div>
      <div class="nx-page-header__titles">
        <p v-if="props.kicker" class="nx-page-header__kicker">{{ props.kicker }}</p>
        <component :is="heading" class="nx-page-header__title">{{ props.title }}</component>
        <p
          v-if="props.liveSubtitle"
          class="nx-page-header__subtitle"
          :class="{ 'is-empty': !props.subtitle }"
          role="status"
          aria-live="polite"
        >
          {{ props.subtitle }}
        </p>
        <p v-else-if="props.subtitle" class="nx-page-header__subtitle">{{ props.subtitle }}</p>
        <div v-if="$slots.default" class="nx-page-header__meta">
          <slot />
        </div>
      </div>
      <div v-if="$slots.actions" class="nx-page-header__actions">
        <slot name="actions" />
      </div>
    </header>
    <div v-if="$slots.toolbar" class="nx-page-header__toolbar">
      <slot name="toolbar" />
    </div>
    <p v-if="props.error" class="nx-page-header__note is-error" role="alert">{{ props.error }}</p>
    <p v-else-if="props.hint" class="nx-page-header__note">{{ props.hint }}</p>
  </div>
</template>

<style scoped>
.nx-page-header {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: var(--nxt-space-md);
  min-width: 0;
}
.nx-page-header__hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--nxt-space-md);
  min-width: 0;
}
.nx-page-header__leading {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-sm);
}
/* The page's icon tile: the brand fill, raised like a mark. */
.nx-page-header__tile {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-tile-size-xl);
  height: var(--nxt-tile-size-xl);
  border-radius: var(--nxt-radius-xl);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  box-shadow: var(--nxt-shadow-lg);
}
.nx-page-header__titles {
  display: flex;
  flex: 1 1 calc(var(--nxt-size-unit) * 16);
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-page-header__kicker {
  margin: 0;
  color: var(--nxt-brand-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.nx-page-header__title {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-bold);
  letter-spacing: -0.02em;
  line-height: var(--nxt-line-height-tight);
}
.nx-page-header__subtitle {
  max-width: 64ch;
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-md);
  line-height: var(--nxt-line-height-normal);
}
.nx-page-header__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-block-start: var(--nxt-space-2xs);
}
.nx-page-header__actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-inline-start: auto;
}
.nx-page-header__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
/* Compact: the app-header size of NxWorkspaceHeader. */
.nx-page-header.is-compact {
  gap: var(--nxt-space-sm);
}
.nx-page-header.is-compact .nx-page-header__hero {
  align-items: center;
  gap: var(--nxt-space-sm);
}
.nx-page-header.is-compact .nx-page-header__title {
  overflow: hidden;
  font-size: var(--nxt-font-size-xl);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-page-header.is-compact .nx-page-header__subtitle {
  max-width: none;
  overflow: hidden;
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-page-header__subtitle.is-empty {
  /* The live region stays in the tree (announcements need it), without a gap. */
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.nx-page-header.is-compact .nx-page-header__tile {
  width: var(--nxt-tile-size-md);
  height: var(--nxt-tile-size-md);
  /* The md tile's radius, as NxListRow's icon tile: one family. */
  border-radius: var(--nxt-radius-lg);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-page-header.is-compact .nx-page-header__actions {
  gap: var(--nxt-space-2xs);
}
/* One gap for the toolbar controls (search, period navigator, view switch). */
.nx-page-header.is-compact .nx-page-header__toolbar {
  gap: var(--nxt-space-xs);
}
.nx-page-header__note {
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-page-header__note.is-error {
  border-color: var(--nxt-danger-border);
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
}
</style>
